import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Folder,
  FolderOpen,
  ExpandMore,
  ChevronRight,
  Description,
  PictureAsPdf,
  Image,
  Architecture,
  Home,
} from '@mui/icons-material';
import { foldersApi, storageApi } from '../../services/fileShareApi';

const FolderTree = ({ projectId, onFolderSelect, selectedFolder, refreshTrigger, onFileSelect, onNavigateHome }) => {
  const [folders, setFolders] = useState([]);
  const [rootFiles, setRootFiles] = useState([]); // Files at root level
  const [folderFilesMap, setFolderFilesMap] = useState(new Map()); // Files in each folder (folderId -> files[])
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  useEffect(() => {
    if (projectId) {
      fetchFolders();
    }
  }, [projectId, refreshTrigger]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching folders for project:', projectId);

      // Fetch unified folder tree from API (includes both UI_CREATED and STORAGE_SYNCED folders)
      const response = await foldersApi.getTreeByProject(projectId);

      console.log('✅ Folder tree response:', response);
      console.log('📁 Folders data:', response.data);
      
      // Debug: Log the actual structure
      if (response.data && Array.isArray(response.data)) {
        console.log('🔍 Total folders:', response.data.length);
        response.data.forEach((folder, idx) => {
          console.log(`🔍 Folder ${idx + 1}: ${folder.folderName}`);
          console.log(`   - folderId: ${folder.folderId}`);
          console.log(`   - subFolders:`, folder.subFolders);
          console.log(`   - has subFolders:`, folder.subFolders && folder.subFolders.length > 0);
          if (folder.subFolders && folder.subFolders.length > 0) {
            console.log(`   - subFolders count: ${folder.subFolders.length}`);
            folder.subFolders.forEach((sub, subIdx) => {
              console.log(`     - SubFolder ${subIdx + 1}: ${sub.folderName}`);
            });
          }
        });
      }

      const folderTree = response.data || [];
      setFolders(folderTree);

      // Do not prefetch unified-files for every folder (that flooded Egnyte).
      // Tree file icons load lazily when a folder is expanded; main pane loads on select.
      setFolderFilesMap(new Map());

      // Root-level files only — DB listing, no per-folder Egnyte walk
      await fetchRootFiles(folderTree);

      setError(null);
    } catch (err) {
      console.error('❌ Error fetching folders:', err);
      setError('Failed to load folders');
      // Show empty folders on error - don't block the UI
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };
  
  const collectFolderIds = (foldersList, ids = new Set()) => {
    for (const folder of foldersList) {
      if (folder.folderId != null) {
        ids.add(String(folder.folderId));
      }
      if (folder.subFolders?.length) {
        collectFolderIds(folder.subFolders, ids);
      }
    }
    return ids;
  };

  const fetchRootFiles = async (folderTree = []) => {
    try {
      const fileResponse = await storageApi.getUnifiedProjectFiles(projectId, '');
      if (fileResponse?.data?.success) {
        const files = fileResponse.data.files || [];
        // Keep tree root free of files that already nest under a folder (avoids duplicates)
        const folderIds = collectFolderIds(folderTree);
        const onlyTrueRootFiles = files.filter((file) => {
          const fileFolderId = file.folderId != null ? String(file.folderId) : null;
          if (!fileFolderId) return true;
          return !folderIds.has(fileFolderId);
        });
        setRootFiles(onlyTrueRootFiles);
        console.log('✅ Fetched root files:', onlyTrueRootFiles.length);
      }
    } catch (err) {
      console.error('❌ Error fetching root files:', err);
      setRootFiles([]);
    }
  };
  
  const fetchFilesForFolder = async (folderId) => {
    if (folderFilesMap.has(folderId)) {
      return;
    }
    try {
      const fileResponse = await storageApi.getUnifiedFolderFiles(projectId, folderId, '');
      if (fileResponse?.data?.success) {
        const files = fileResponse.data.files || [];
        setFolderFilesMap((prev) => {
          const next = new Map(prev);
          next.set(folderId, files);
          return next;
        });
        console.log(`✅ Lazy-loaded files for folder ${folderId}:`, files.length);
      }
    } catch (err) {
      console.error(`❌ Error fetching files for folder ${folderId}:`, err);
      setFolderFilesMap((prev) => {
        const next = new Map(prev);
        next.set(folderId, []);
        return next;
      });
    }
  };
  
  const getFileIcon = (fileType) => {
    switch (fileType?.toUpperCase()) {
      case 'RVT':
      case 'DWG':
        return <Architecture sx={{ fontSize: 20 }} />;
      case 'PDF':
        return <PictureAsPdf sx={{ fontSize: 20 }} />;
      case 'IMAGE':
      case 'JPG':
      case 'JPEG':
      case 'PNG':
      case 'GIF':
        return <Image sx={{ fontSize: 20 }} />;
      default:
        return <Description sx={{ fontSize: 20 }} />;
    }
  };

  const handleToggleExpand = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
        // Lazy-load DB files for tree icons when expanding
        fetchFilesForFolder(folderId);
      }
      return newSet;
    });
  };

  const handleFolderClick = (folder) => {
    onFolderSelect(folder);
  };

  const renderFile = (file, level = 0) => {
    // Only check file selection, not folder selection
    const isSelected = false; // Files are selected via onFileSelect, not through selectedFolder prop
    
    return (
      <ListItem 
        key={file.fileId || file.id || `file_${file.name}`} 
        disablePadding
      >
        <ListItemButton
          onClick={() => {
            if (onFileSelect) {
              onFileSelect(file);
            }
          }}
          selected={isSelected}
          sx={{
            pl: 2 + level * 2,
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)', // Light blue, calmer tone
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
              '& .MuiListItemText-primary': {
                color: 'primary.main',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Box sx={{ width: 32 }} />
          </ListItemIcon>
          
          <ListItemIcon sx={{ minWidth: 32 }}>
            {getFileIcon(file.type || file.fileType)}
          </ListItemIcon>
          
          <ListItemText
            primary={file.fileName || file.name}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'text.primary',
              noWrap: true,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderFolder = (folder, level = 0) => {
    const isExpanded = expandedFolders.has(folder.folderId);
    const hasSubFolders = folder.subFolders && folder.subFolders.length > 0;
    const filesLoaded = folderFilesMap.has(folder.folderId);
    const folderFiles = folderFilesMap.get(folder.folderId) || [];
    const hasFiles = folderFiles.length > 0;
    // Always allow expand so file-only folders can lazy-load children
    const canExpand = hasSubFolders || !filesLoaded || hasFiles;
    const isSelected = selectedFolder?.folderId === folder.folderId;

    return (
      <Box key={folder.folderId}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleFolderClick(folder)}
            selected={isSelected}
            sx={{
              pl: 2 + level * 2,
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)', // Light blue, calmer tone
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.16)',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
                '& .MuiListItemText-primary': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {canExpand ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(folder.folderId);
                  }}
                  sx={{
                    color: 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {isExpanded ? <ExpandMore /> : <ChevronRight />}
                </IconButton>
              ) : (
                <Box sx={{ width: 32 }} />
              )}
            </ListItemIcon>
            
            <ListItemIcon sx={{ minWidth: 32 }}>
              {isExpanded ? <FolderOpen /> : <Folder />}
            </ListItemIcon>
            
            <ListItemText
              primary={folder.folderName}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isSelected ? 600 : 500,
                color: 'text.primary',
                noWrap: true,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
          </ListItemButton>
        </ListItem>
        
        {canExpand && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {/* Render subfolders */}
              {folder.subFolders?.map(subFolder => renderFolder(subFolder, level + 1))}
              {/* Render files in this folder */}
              {folderFiles.map(file => renderFile(file, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600, 
            fontSize: '0.875rem',
            color: 'text.primary'
          }}
        >
          Folders
        </Typography>
      </Box>
      
      {folders.length === 0 && rootFiles.length === 0 ? (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            textAlign: 'center', 
            py: 4, 
            px: 2,
            fontSize: '0.8125rem'
          }}
        >
          No folders or files yet. Use Sync Folders to bring folders in from storage.
        </Typography>
      ) : (
        <List dense sx={{ pt: 0 }}>
          {/* Render root files */}
          {rootFiles.map(file => renderFile(file, 0))}
          {/* Render folders */}
          {folders.map(folder => renderFolder(folder))}
        </List>
      )}
    </Box>
  );
};

export default FolderTree;

